import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, ChevronRight, ChevronLeft, Play, MousePointer2 } from 'lucide-react';

const WALKTHROUGH_STEPS = [
  // --- WELCOME ---
  {
    page: '/',
    target: '[data-testid="user-menu-trigger"]',
    title: 'Welcome to CMS!',
    description: "Let's walk you through the app. This is your user menu — access settings, user management, and more from here.",
    position: 'bottom',
  },
  // --- HOME / DASHBOARD ---
  {
    page: '/',
    target: '[data-testid="dashboard-title"]',
    title: 'Dashboard Overview',
    description: 'This is your command center. See claim totals, open cases, and key statistics at a glance.',
    position: 'bottom',
  },
  {
    page: '/',
    target: '[data-testid="active-companies-header"]',
    title: 'Company Filter',
    description: 'Click here to filter the dashboard by a specific company or view all companies combined.',
    position: 'bottom',
  },
  {
    page: '/',
    target: '[data-testid="date-filter-trigger"]',
    title: 'Date Range Filter',
    description: 'Narrow down your data by date range — use presets like "Last 30 Days" or set custom dates.',
    position: 'bottom',
  },
  {
    page: '/',
    target: '[data-testid="loss-type-bar"]',
    title: 'Loss Type Breakdown',
    description: 'See claims distributed by loss type. Hover over bars for exact counts.',
    position: 'top',
  },
  // --- INTAKE FORM ---
  {
    page: '/intake',
    target: '[data-testid="intake-form"]',
    title: 'Claims Intake Form',
    description: 'Submit new claims using this guided multi-step form. Start by selecting the company and loss type.',
    position: 'bottom',
    fallbackTarget: '.min-h-screen',
  },
  // --- ALL ENTRIES ---
  {
    page: '/all-entries',
    target: '[data-testid="all-entries-title"]',
    title: 'All Claims Table',
    description: 'Every claim across all companies in one place. Search, filter by status, and click any row to view details.',
    position: 'bottom',
  },
  {
    page: '/all-entries',
    target: '[data-testid="search-input"]',
    title: 'Search Claims',
    description: 'Search by report number, company name, driver, or any claim detail. Results update instantly.',
    position: 'bottom',
  },
  {
    page: '/all-entries',
    target: '[data-testid="status-filter"]',
    title: 'Filter by Status',
    description: 'Filter claims by their current status — New, In Process, or Closed.',
    position: 'bottom',
  },
  // --- COMPANIES (with interactive side panel + notes) ---
  {
    page: '/companies',
    target: '[data-testid="company-name-display"]',
    title: 'Company View',
    description: 'View claims for a specific company. See loss type stats, manage documents, and add notes per claim.',
    position: 'bottom',
    fallbackTarget: '.min-h-screen',
  },
  // --- SIDE PANEL (on Companies page where EditClaimPanel lives) ---
  {
    page: '/companies',
    target: '[data-testid="edit-panel-title"]',
    title: 'Claim Side Panel',
    description: 'Click any row to open the detail panel. View and edit claim info, change status, manage documents, and more.',
    position: 'bottom',
    action: { type: 'click', selector: 'tr[data-testid^="company-claim-row-"]' },
    fallbackTarget: '[data-testid="edit-claim-panel"]',
  },
  // --- NOTES MODAL (opened from EditClaimPanel on Companies page) ---
  {
    page: '/companies',
    target: '[data-testid="notes-modal"]',
    title: 'Notes & Comments',
    description: 'Add notes and comments to any claim. Collaborate with your team by tracking updates and decisions.',
    position: 'top',
    action: { type: 'sequence', steps: [
      { selector: 'tr[data-testid^="company-claim-row-"]', delay: 0 },
      { selector: '[data-testid="edit-tab-notes"]', delay: 900 },
    ]},
    fallbackTarget: '[data-testid="edit-claim-panel"]',
  },
  // --- COMPANY MANAGEMENT ---
  {
    page: '/admin',
    target: '[data-testid="add-company-btn"]',
    title: 'Company Management',
    description: 'Add, edit, or remove the companies you manage claims for. This is where you set up your workspace.',
    position: 'bottom',
    fallbackTarget: '.min-h-screen',
  },
  // --- SETTINGS / BILLING ---
  {
    page: '/settings?tab=billing',
    target: '[data-testid="tab-billing"]',
    title: 'Billing & Subscription',
    description: 'Manage your subscription, view usage limits, and upgrade your plan. Start with a 30-day free trial.',
    position: 'bottom',
    fallbackTarget: '[data-testid="settings-tabs"]',
  },
];

const STORAGE_KEY = 'demo_walkthrough_active';
const STEP_KEY = 'demo_walkthrough_step';
const SEEN_KEY = 'demo_walkthrough_seen';

const getTooltipPosition = (rect, position, viewportW, viewportH) => {
  const gap = 14;
  const style = { position: 'fixed', zIndex: 10011 };
  const tooltipW = Math.min(320, viewportW - 32);

  if (position === 'bottom') {
    style.top = Math.min(rect.bottom + gap, viewportH - 200);
    style.left = Math.max(16, Math.min(rect.left + rect.width / 2 - tooltipW / 2, viewportW - tooltipW - 16));
  } else if (position === 'top') {
    style.top = Math.max(16, rect.top - gap - 180);
    style.left = Math.max(16, Math.min(rect.left + rect.width / 2 - tooltipW / 2, viewportW - tooltipW - 16));
  }
  return style;
};

export default function DemoWalkthrough() {
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [cursorPos, setCursorPos] = useState(null);
  const [transitioning, setTransitioning] = useState(false);

  // Restore state across page navigations OR auto-start on first demo login
  useEffect(() => {
    const isActive = sessionStorage.getItem(STORAGE_KEY) === 'true';
    const savedStep = parseInt(sessionStorage.getItem(STEP_KEY) || '0', 10);
    if (isActive) {
      setActive(true);
      setStep(savedStep);
      return;
    }
    // Auto-start on first visit (never seen before)
    const hasSeen = localStorage.getItem(SEEN_KEY);
    if (!hasSeen) {
      localStorage.setItem(SEEN_KEY, 'true');
      sessionStorage.setItem(STORAGE_KEY, 'true');
      sessionStorage.setItem(STEP_KEY, '0');
      setActive(true);
      setStep(0);
    }
  }, []);

  // Find and highlight target element when step or page changes
  useEffect(() => {
    if (!active) return;
    const currentStep = WALKTHROUGH_STEPS[step];
    if (!currentStep) return;

    const currentPath = location.pathname + location.search;
    const stepPath = currentStep.page;

    // Navigate if needed
    if (!currentPath.startsWith(stepPath.split('?')[0])) {
      setTransitioning(true);
      navigate(stepPath);
      return;
    }

    setTransitioning(false);
    const timers = [];

    // Execute actions (single click or sequence)
    const executeActions = () => {
      if (!currentStep.action) return;
      if (currentStep.action.type === 'click') {
        const el = document.querySelector(currentStep.action.selector);
        if (el) el.click();
      } else if (currentStep.action.type === 'sequence') {
        currentStep.action.steps.forEach((s, i) => {
          const t = setTimeout(() => {
            const el = document.querySelector(s.selector);
            if (el) el.click();
          }, s.delay);
          timers.push(t);
        });
      }
    };

    // For action steps: execute action first, then wait for UI to settle before finding target
    // For non-action steps: find target immediately with retries
    const hasAction = !!currentStep.action;
    const findDelays = hasAction ? [1000, 1600, 2400, 3500] : [300, 700, 1200, 2000, 3000];
    let found = false;

    if (hasAction) {
      const actionTimer = setTimeout(executeActions, 150);
      timers.push(actionTimer);
    }

    const tryFind = (i) => {
      if (i >= findDelays.length || found) return;
      const t = setTimeout(() => {
        const el = document.querySelector(currentStep.target) ||
          (currentStep.fallbackTarget ? document.querySelector(currentStep.fallbackTarget) : null);
        if (el) {
          found = true;
          const rect = el.getBoundingClientRect();
          setCursorPos({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
          // Re-capture fresh rect after animations settle
          const rectTimer = setTimeout(() => {
            const freshRect = el.getBoundingClientRect();
            setTargetRect(freshRect);
          }, 350);
          timers.push(rectTimer);
        } else {
          tryFind(i + 1);
        }
      }, findDelays[i]);
      timers.push(t);
    };
    tryFind(0);

    return () => { timers.forEach(clearTimeout); };
  }, [active, step, location.pathname, location.search, navigate]);

  const startWalkthrough = useCallback(() => {
    setStep(0);
    setActive(true);
    setTargetRect(null);
    setCursorPos(null);
    sessionStorage.setItem(STORAGE_KEY, 'true');
    sessionStorage.setItem(STEP_KEY, '0');
    navigate('/');
  }, [navigate]);

  const closeOpenPanels = useCallback(() => {
    // Close notes modal if open
    const notesExit = document.querySelector('[data-testid="notes-modal-exit"]');
    if (notesExit) notesExit.click();
    // Close edit panel if open (EditClaimPanel or EditEntryPanel)
    const panelClose = document.querySelector('[data-testid="edit-panel-close"]') ||
      document.querySelector('[data-testid="edit-entry-close"]');
    if (panelClose) panelClose.click();
  }, []);

  const endWalkthrough = useCallback(() => {
    closeOpenPanels();
    setActive(false);
    setTargetRect(null);
    setCursorPos(null);
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STEP_KEY);
    navigate('/');
  }, [closeOpenPanels, navigate]);

  const goToStep = useCallback((newStep) => {
    if (newStep < 0 || newStep >= WALKTHROUGH_STEPS.length) {
      endWalkthrough();
      return;
    }
    // Always close open panels/modals before transitioning
    closeOpenPanels();
    setTargetRect(null);
    setCursorPos(null);
    setStep(newStep);
    sessionStorage.setItem(STEP_KEY, String(newStep));
  }, [endWalkthrough, closeOpenPanels]);

  // Listen for custom event from Navbar to start tour
  useEffect(() => {
    const handler = () => startWalkthrough();
    window.addEventListener('start-demo-tour', handler);
    return () => window.removeEventListener('start-demo-tour', handler);
  }, [startWalkthrough]);

  if (!active) {
    return (
      <button
        onClick={startWalkthrough}
        className="hidden md:flex fixed bottom-6 right-16 z-[9998] items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white text-xs font-medium pl-3 pr-4 py-2.5 rounded-full shadow-lg transition-all hover:scale-105"
        data-testid="walkthrough-start-btn"
        title="Start interactive demo tour"
      >
        <Play className="w-3.5 h-3.5" />
        Demo Tour
      </button>
    );
  }

  const currentStep = WALKTHROUGH_STEPS[step];
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const tooltipStyle = targetRect ? getTooltipPosition(targetRect, currentStep.position, vw, vh) : {};
  const pageName = currentStep?.page === '/' ? 'Dashboard' :
    currentStep?.page === '/intake' ? 'Intake Form' :
    currentStep?.page === '/all-entries' ? 'All Entries' :
    currentStep?.page === '/companies' ? 'Companies' :
    currentStep?.page === '/admin' ? 'Company Management' :
    currentStep?.page?.includes('billing') ? 'Billing' : 'Page';

  return (
    <>
      {/* Animated cursor */}
      {cursorPos && (
        <div
          className="fixed z-[10015] pointer-events-none transition-all duration-500 ease-out"
          style={{ top: cursorPos.y - 12, left: cursorPos.x - 4 }}
        >
          <MousePointer2 className="w-6 h-6 text-teal-500 drop-shadow-lg" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
        </div>
      )}

      {/* Overlay - click backdrop to end tour */}
      {targetRect && !transitioning && (
        <div className="fixed inset-0 z-[10009] pointer-events-none">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <mask id="wt-mask">
                <rect width="100%" height="100%" fill="white" />
                <rect
                  x={targetRect.left - 8}
                  y={targetRect.top - 6}
                  width={targetRect.width + 16}
                  height={targetRect.height + 12}
                  rx="10"
                  fill="black"
                />
              </mask>
            </defs>
            <rect width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#wt-mask)" className="pointer-events-auto cursor-pointer" onClick={endWalkthrough} />
          </svg>
        </div>
      )}

      {/* Highlight ring */}
      {targetRect && !transitioning && (
        <div
          className="fixed z-[10010] pointer-events-none rounded-xl ring-2 ring-teal-400 ring-offset-2 transition-all duration-500 ease-out"
          style={{
            top: targetRect.top - 6,
            left: targetRect.left - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 12,
          }}
        />
      )}

      {/* Tooltip */}
      {targetRect && !transitioning && (
        <div
          style={{...tooltipStyle, pointerEvents: 'auto'}}
          className="w-[calc(100vw-2rem)] sm:w-80 max-w-80"
          data-testid="walkthrough-tooltip"
        >
          <div className="bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
            {/* Page indicator */}
            <div className="bg-gray-900 px-4 py-2 flex items-center justify-between">
              <span className="text-[10px] font-semibold text-teal-400 uppercase tracking-wider">{pageName}</span>
              <span className="text-[10px] text-gray-400">{step + 1} / {WALKTHROUGH_STEPS.length}</span>
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between mb-1.5">
                <h3 className="font-semibold text-gray-900 text-sm">{currentStep.title}</h3>
                <button onClick={endWalkthrough} className="text-gray-400 hover:text-gray-600 -mt-0.5 -mr-1" data-testid="walkthrough-close">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[13px] text-gray-500 leading-relaxed mb-4">{currentStep.description}</p>
              <div className="flex items-center justify-between">
                <button
                  onClick={endWalkthrough}
                  className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                  data-testid="walkthrough-skip"
                >
                  End Tour
                </button>
                <div className="flex gap-1.5">
                  {step > 0 && (
                    <button
                      onClick={() => goToStep(step - 1)}
                      className="flex items-center gap-0.5 text-gray-500 hover:text-gray-700 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-200 transition-colors"
                      data-testid="walkthrough-prev"
                    >
                      <ChevronLeft className="w-3 h-3" />
                      Back
                    </button>
                  )}
                  <button
                    onClick={() => goToStep(step + 1)}
                    className="flex items-center gap-1 bg-teal-500 hover:bg-teal-600 text-white text-xs font-medium px-4 py-1.5 rounded-lg transition-colors"
                    data-testid="walkthrough-next"
                  >
                    {step === WALKTHROUGH_STEPS.length - 1 ? 'Finish' : 'Next'}
                    {step < WALKTHROUGH_STEPS.length - 1 && <ChevronRight className="w-3 h-3" />}
                  </button>
                </div>
              </div>
              {/* Progress bar */}
              <div className="mt-3 h-1 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-teal-500 rounded-full transition-all duration-500"
                  style={{ width: `${((step + 1) / WALKTHROUGH_STEPS.length) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading state during page transition */}
      {transitioning && (
        <div className="fixed inset-0 z-[10009] bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-xl px-6 py-4 flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-gray-600">Navigating to {pageName}...</span>
          </div>
        </div>
      )}
    </>
  );
}
